/**
 * Three Card Stud Poker - REAL Poker with Blinds & Betting Rounds
 * Proper poker: Small/Big Blind, Call/Raise/Fold, Multiple betting rounds
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, HelpCircle, Users, Bot, Coins, X, Trophy, TrendingUp, CircleDot } from 'lucide-react';
import { Button } from '../../components/ui/button';
import useThreeCardStore from './threeCardStore.js';
import { SUITS, BOOSTY_LOGO_URL } from './pokerConstants.js';
import { sortByRank } from './pokerEngine.js';

// Poker Card Component
const PokerCard = ({ card, faceDown, small, delay = 0, highlight, winner }) => {
  if (faceDown) {
    return (
      <motion.div
        initial={{ opacity: 0, rotateY: 180, y: -30 }}
        animate={{ opacity: 1, rotateY: 0, y: 0 }}
        transition={{ duration: 0.4, delay, type: 'spring', damping: 15 }}
        className={`${small ? 'w-14 h-20' : 'w-20 h-28'} rounded-xl relative overflow-hidden`}
        style={{
          background: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 25%, #4c1d95 50%, #2e1065 75%, #1e1b4b 100%)',
          border: '2px solid #a855f7',
          boxShadow: '0 4px 16px rgba(139,92,246,0.3)'
        }}
      >
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-orange-400/40 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-orange-400/40 to-transparent" />
        <div className="absolute inset-0 flex items-center justify-center">
          <img src={BOOSTY_LOGO_URL} alt="Boosty" className={`${small ? 'h-10' : 'h-16'} w-auto object-contain`} style={{ filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.4))' }} />
        </div>
      </motion.div>
    );
  }
  
  const suitInfo = SUITS[card.suit];
  
  return (
    <motion.div
      initial={{ opacity: 0, rotateY: 180, y: -30 }}
      animate={{ opacity: 1, rotateY: 0, y: 0 }}
      transition={{ duration: 0.4, delay, type: 'spring', damping: 15 }}
      className={`${small ? 'w-14 h-20' : 'w-20 h-28'} rounded-xl relative bg-white ${highlight ? 'ring-4 ring-amber-400' : ''} ${winner ? 'ring-4 ring-green-400 shadow-[0_0_20px_rgba(34,197,94,0.5)]' : ''}`}
      style={{ border: '2px solid #cbd5e1', boxShadow: highlight || winner ? '0 8px 24px rgba(0,0,0,0.3)' : '0 4px 12px rgba(0,0,0,0.15)' }}
    >
      <div className="absolute top-1 left-1.5 flex flex-col items-center leading-none">
        <span className={`${small ? 'text-sm' : 'text-lg'} font-black`} style={{ color: suitInfo.color }}>{card.display}</span>
        <span className={`${small ? 'text-xs' : 'text-sm'}`} style={{ color: suitInfo.color }}>{suitInfo.symbol}</span>
      </div>
      <div className="absolute bottom-1 right-1.5 flex flex-col items-center leading-none rotate-180">
        <span className={`${small ? 'text-sm' : 'text-lg'} font-black`} style={{ color: suitInfo.color }}>{card.display}</span>
        <span className={`${small ? 'text-xs' : 'text-sm'}`} style={{ color: suitInfo.color }}>{suitInfo.symbol}</span>
      </div>
    </motion.div>
  );
};

// Player Seat Component
const PlayerSeat = ({ player, isCurrentPlayer, showCards, isWinner, position }) => {
  const positionClasses = {
    bottom: 'bottom-4 left-1/2 -translate-x-1/2',
    top: 'top-4 left-1/2 -translate-x-1/2',
    left: 'left-4 top-1/2 -translate-y-1/2',
    right: 'right-4 top-1/2 -translate-y-1/2'
  };
  
  const sorted = player.cards.length > 0 ? sortByRank(player.cards) : [];
  
  return (
    <div className={`absolute ${positionClasses[position]} flex flex-col items-center gap-2`}>
      {/* Player info */}
      <div className={`px-3 py-1.5 rounded-lg ${isCurrentPlayer ? 'bg-amber-500/30 border-2 border-amber-400' : player.folded ? 'bg-red-900/30 border border-red-800' : 'bg-slate-800/80 border border-slate-600'}`}>
        <div className="flex items-center gap-2">
          {player.isDealer && <span className="w-5 h-5 rounded-full bg-amber-500 text-[10px] font-bold flex items-center justify-center text-black">D</span>}
          {player.isSmallBlind && <span className="w-5 h-5 rounded-full bg-blue-500 text-[10px] font-bold flex items-center justify-center text-white">SB</span>}
          {player.isBigBlind && <span className="w-5 h-5 rounded-full bg-purple-500 text-[10px] font-bold flex items-center justify-center text-white">BB</span>}
          <span className={`text-sm font-bold ${player.folded ? 'text-red-400 line-through' : isWinner ? 'text-green-400' : 'text-white'}`}>
            {player.name}
          </span>
        </div>
        <div className="flex items-center justify-center gap-2 text-xs">
          <span className="text-amber-400">{player.chips}</span>
          {player.currentBet > 0 && <span className="text-cyan-400">Bet: {player.currentBet}</span>}
          {player.isAllIn && <span className="text-red-400 font-bold">ALL IN</span>}
        </div>
      </div>
      
      {/* Cards */}
      {player.cards.length > 0 && (
        <div className="flex -space-x-4">
          {sorted.map((card, idx) => (
            <div key={card.id} style={{ zIndex: idx }}>
              <PokerCard
                card={card}
                faceDown={!showCards && !player.isHuman}
                small={!player.isHuman}
                delay={idx * 0.1}
                winner={isWinner}
              />
            </div>
          ))}
        </div>
      )}
      
      {/* Hand name */}
      {showCards && player.hand && !player.folded && (
        <motion.div
          initial={{ opacity: 0, y: 5 }}
          animate={{ opacity: 1, y: 0 }}
          className={`px-3 py-1 rounded-lg text-sm font-bold ${isWinner ? 'bg-green-500 text-white' : 'bg-slate-700 text-slate-200'}`}
        >
          {player.hand.hand.name}
        </motion.div>
      )}
      
      {player.folded && (
        <span className="text-red-400 text-sm font-semibold">FOLDED</span>
      )}
    </div>
  );
};

// Betting Controls
const BettingControls = ({ player, validActions, currentBet, minRaise, bigBlind, onAction }) => {
  const [raiseAmount, setRaiseAmount] = useState(minRaise);
  const toCall = currentBet - player.currentBet;
  
  useEffect(() => {
    setRaiseAmount(minRaise);
  }, [minRaise]);
  
  const canFold = validActions.includes('fold');
  const canCheck = validActions.includes('check');
  const canCall = validActions.includes('call');
  const canRaise = validActions.includes('raise');
  const canAllIn = validActions.includes('all_in');
  
  return (
    <div className="bg-slate-900/95 backdrop-blur-sm rounded-xl p-4 border-2 border-amber-500/50 shadow-2xl">
      <h3 className="text-amber-400 text-sm font-bold mb-3 flex items-center gap-2">
        <CircleDot className="w-4 h-4 animate-pulse" />
        Your Action
      </h3>
      
      {toCall > 0 && (
        <div className="text-sm text-slate-400 mb-3">
          {toCall} to call
        </div>
      )}
      
      <div className="flex flex-wrap gap-2 mb-3">
        {canFold && (
          <Button onClick={() => onAction('fold')} variant="outline" className="border-red-600 text-red-400 hover:bg-red-600/20">
            Fold
          </Button>
        )}
        {canCheck && (
          <Button onClick={() => onAction('check')} variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-600/20">
            Check
          </Button>
        )}
        {canCall && (
          <Button onClick={() => onAction('call')} className="bg-blue-600 hover:bg-blue-500 text-white">
            Call {toCall}
          </Button>
        )}
        {canAllIn && (
          <Button onClick={() => onAction('all_in')} className="bg-red-600 hover:bg-red-500 text-white">
            All In ({player.chips})
          </Button>
        )}
      </div>
      
      {canRaise && (
        <div className="flex items-center gap-2">
          <input
            type="range"
            min={minRaise}
            max={Math.max(minRaise, player.chips - toCall)}
            value={raiseAmount}
            onChange={(e) => setRaiseAmount(parseInt(e.target.value))}
            className="flex-1 accent-amber-500"
          />
          <input
            type="number"
            value={raiseAmount}
            onChange={(e) => setRaiseAmount(Math.max(minRaise, parseInt(e.target.value) || minRaise))}
            className="w-20 bg-slate-800 border border-slate-600 rounded px-2 py-1 text-white text-sm"
          />
          <Button onClick={() => onAction('raise', raiseAmount)} className="bg-amber-600 hover:bg-amber-500 text-white">
            Raise to {player.currentBet + toCall + raiseAmount}
          </Button>
        </div>
      )}
    </div>
  );
};

// Action Log
const ActionLog = ({ log }) => (
  <div className="bg-slate-900/80 rounded-xl p-3 border border-slate-700 max-h-40 overflow-y-auto">
    <h4 className="text-xs text-slate-500 font-semibold mb-2">Action Log</h4>
    <div className="space-y-1">
      {log.slice(-8).map((entry, idx) => (
        <div key={idx} className="text-xs text-slate-400">{entry}</div>
      ))}
    </div>
  </div>
);

// Mode Selector
const ModeSelector = ({ onSelectMode }) => {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-emerald-950 to-slate-950 flex items-center justify-center p-4">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <Button variant="ghost" onClick={() => navigate('/dashboard')} className="absolute top-4 left-4 text-gray-400 hover:text-white">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </Button>
        
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">Three Card Poker</h1>
          <p className="text-gray-400">Real poker with blinds & betting</p>
        </div>
        
        <div className="space-y-4">
          <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} onClick={() => onSelectMode('ai_opponents')}
            className="w-full bg-slate-900/90 backdrop-blur-sm rounded-xl border border-slate-700 p-6 text-left hover:border-cyan-500/50 transition-all group">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-cyan-600/20 flex items-center justify-center group-hover:bg-cyan-600/30 transition-colors">
                <Bot className="w-7 h-7 text-cyan-400" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-white mb-1">4-Player Table</h2>
                <p className="text-sm text-gray-400">You vs 3 AI opponents</p>
              </div>
            </div>
          </motion.button>
          
          <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} onClick={() => onSelectMode('dealer')}
            className="w-full bg-slate-900/90 backdrop-blur-sm rounded-xl border border-slate-700 p-6 text-left hover:border-amber-500/50 transition-all group">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-amber-600/20 flex items-center justify-center group-hover:bg-amber-600/30 transition-colors">
                <Coins className="w-7 h-7 text-amber-400" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-white mb-1">Heads Up</h2>
                <p className="text-sm text-gray-400">1v1 against AI</p>
              </div>
            </div>
          </motion.button>
          
          <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} disabled
            className="w-full bg-slate-900/90 backdrop-blur-sm rounded-xl border border-slate-700 p-6 text-left opacity-50 cursor-not-allowed">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-purple-600/20 flex items-center justify-center">
                <Users className="w-7 h-7 text-purple-400" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-white mb-1">Multiplayer</h2>
                <p className="text-sm text-gray-400">Coming soon...</p>
              </div>
            </div>
          </motion.button>
        </div>
        
        <div className="mt-8 bg-slate-900/50 rounded-xl border border-slate-700/50 p-4">
          <h3 className="text-sm font-semibold text-amber-400 mb-2">How It Works</h3>
          <ul className="text-sm text-gray-400 space-y-1">
            <li>• Blinds: Small (5) & Big (10) posted each hand</li>
            <li>• Each player gets 3 cards</li>
            <li>• Betting round: Fold, Call, Raise, or All-In</li>
            <li>• Best 3-card poker hand wins the pot</li>
          </ul>
        </div>
      </motion.div>
    </div>
  );
};

// Main Game Component
const ThreeCardStud = () => {
  const navigate = useNavigate();
  const [showHelp, setShowHelp] = useState(false);
  
  const {
    gameMode, gamePhase, players, currentPlayerIndex, pot, currentBet, minRaise, bigBlind,
    actionLog, showdownRevealed, winners, message, handsPlayed, handsWon,
    setGameMode, startHand, playerAction, getValidActions, nextHand, resetGame
  } = useThreeCardStore();
  
  if (!gameMode) {
    return <ModeSelector onSelectMode={setGameMode} />;
  }
  
  const humanPlayer = players.find(p => p.isHuman);
  const isHumanTurn = humanPlayer && players[currentPlayerIndex]?.isHuman && gamePhase === 'betting';
  const validActions = isHumanTurn ? getValidActions() : [];
  
  // Position players around table
  const getPlayerPosition = (index, total) => {
    if (total === 2) return index === 0 ? 'bottom' : 'top';
    if (total === 4) return ['bottom', 'left', 'top', 'right'][index];
    return 'bottom';
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-emerald-950 to-slate-950">
      {/* Header */}
      <div className="bg-slate-900/80 backdrop-blur-sm border-b-2 border-slate-700/50 px-4 py-3">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => resetGame()} className="text-slate-400 hover:text-white hover:bg-slate-800">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <div>
              <h1 className="text-xl font-black text-white">Three Card Poker</h1>
              <p className="text-sm text-slate-400">Blinds: 5/10</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Coins className="w-5 h-5 text-amber-400" />
              <span className="font-bold text-white">{humanPlayer?.chips || 0}</span>
            </div>
            <div className="text-right text-sm">
              <div className="text-slate-400">W/L</div>
              <div className="text-white font-bold">{handsWon}/{handsPlayed}</div>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setShowHelp(true)} className="text-slate-400 hover:text-white">
              <HelpCircle className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
      
      {/* Game Area */}
      <div className="max-w-6xl mx-auto p-4">
        <div className="grid grid-cols-12 gap-4">
          {/* Left sidebar - Action log */}
          <div className="col-span-3 space-y-4">
            <ActionLog log={actionLog} />
            
            {/* Betting controls for human */}
            {isHumanTurn && humanPlayer && (
              <BettingControls
                player={humanPlayer}
                validActions={validActions}
                currentBet={currentBet}
                minRaise={minRaise}
                bigBlind={bigBlind}
                onAction={playerAction}
              />
            )}
          </div>
          
          {/* Main table */}
          <div className="col-span-9">
            <div 
              className="relative rounded-3xl border-4 border-emerald-600/50 p-6 min-h-[500px]"
              style={{
                background: 'radial-gradient(ellipse at center, #166534 0%, #14532d 40%, #052e16 100%)',
                boxShadow: '0 12px 48px rgba(0,0,0,0.4)'
              }}
            >
              {/* Message */}
              <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10">
                <div className="bg-slate-900/90 px-6 py-2 rounded-xl border border-slate-600">
                  <p className="text-white font-semibold">{message}</p>
                </div>
              </div>
              
              {/* Pot */}
              {pot > 0 && (
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                  <div className="bg-amber-900/60 px-6 py-3 rounded-xl border-2 border-amber-500/50">
                    <span className="text-amber-300 font-bold text-xl">Pot: {pot}</span>
                  </div>
                </div>
              )}
              
              {/* Player seats */}
              {players.map((player, idx) => (
                <PlayerSeat
                  key={player.id}
                  player={player}
                  isCurrentPlayer={idx === currentPlayerIndex && gamePhase === 'betting'}
                  showCards={showdownRevealed || player.isHuman}
                  isWinner={winners.some(w => w.id === player.id)}
                  position={getPlayerPosition(idx, players.length)}
                />
              ))}
              
              {/* Deal / Next Hand buttons */}
              {gamePhase === 'waiting' && (
                <div className="absolute bottom-20 left-1/2 -translate-x-1/2">
                  <Button onClick={startHand} className="bg-amber-600 hover:bg-amber-500 text-white font-bold px-8 py-3 text-lg">
                    Deal
                  </Button>
                </div>
              )}
              
              {gamePhase === 'result' && (
                <div className="absolute bottom-20 left-1/2 -translate-x-1/2">
                  <Button onClick={nextHand} className="bg-amber-600 hover:bg-amber-500 text-white font-bold px-8 py-3 text-lg">
                    Next Hand
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Help Modal */}
      <AnimatePresence>
        {showHelp && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setShowHelp(false)}>
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }}
              className="bg-slate-900 border-2 border-slate-700 rounded-2xl p-6 max-w-lg" onClick={e => e.stopPropagation()}>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-white">How to Play</h2>
                <button onClick={() => setShowHelp(false)} className="text-slate-400 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-4 text-gray-300 text-sm">
                <div className="bg-slate-800/50 rounded-xl p-4">
                  <h3 className="text-amber-400 font-semibold mb-2">Game Flow</h3>
                  <ol className="list-decimal list-inside space-y-1">
                    <li>Small Blind (5) and Big Blind (10) are posted</li>
                    <li>Each player receives 3 cards</li>
                    <li>Betting round: Fold, Check, Call, Raise, or All-In</li>
                    <li>After betting completes, hands are revealed</li>
                    <li>Best hand wins the pot!</li>
                  </ol>
                </div>
                
                <div className="bg-slate-800/50 rounded-xl p-4">
                  <h3 className="text-cyan-400 font-semibold mb-2">Hand Rankings (Best to Worst)</h3>
                  <ol className="list-decimal list-inside space-y-1">
                    <li>Straight Flush - 3 consecutive, same suit</li>
                    <li>Three of a Kind - 3 same rank</li>
                    <li>Straight - 3 consecutive</li>
                    <li>Flush - 3 same suit</li>
                    <li>Pair - 2 same rank</li>
                    <li>High Card</li>
                  </ol>
                </div>
                
                <div className="bg-slate-800/50 rounded-xl p-4">
                  <h3 className="text-green-400 font-semibold mb-2">Betting Actions</h3>
                  <ul className="space-y-1">
                    <li><span className="text-red-400">Fold</span> - Give up your hand</li>
                    <li><span className="text-slate-300">Check</span> - Pass (if no bet)</li>
                    <li><span className="text-blue-400">Call</span> - Match the current bet</li>
                    <li><span className="text-amber-400">Raise</span> - Increase the bet</li>
                    <li><span className="text-red-400">All-In</span> - Bet all your chips</li>
                  </ul>
                </div>
              </div>
              
              <Button onClick={() => setShowHelp(false)} className="w-full mt-4 bg-slate-700 hover:bg-slate-600">
                Got it!
              </Button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ThreeCardStud;
